/* Anemoia desktop FX (custom, shared): the WebGL CRT curvature pass (same
   shader as the About/World monitor dive in anemoia-about.js) + the per-mode
   collection-world backdrop sync. Used by the Letters page and the CRT desktop
   page, which are plain (non-module) scripts — so this exposes a global. */
(() => {
  /**
   * Original WebGL CRT curvature pass. Draws a transparent-black full-screen
   * overlay whose sampling space is barrel-curved, so the scanlines, aperture
   * grille, vignette and rolling band all bow like a real tube and the corners
   * fall outside into a curved black bezel — selling the bulge while the text
   * underneath stays crisp. Returns null when WebGL is unavailable (the CSS
   * glass then stands in). One full-screen triangle + a cheap shader at capped
   * DPR, paused on hidden tabs, so the GPU cost is minimal.
   * @param {HTMLCanvasElement} canvas
   */
  function createCrtPass(canvas) {
    const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: false });
    if (!gl) return null;

    const VS = 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }';
    const FS = `
      precision mediump float;
      uniform vec2 u_res;
      uniform float u_time;
      void main() {
        vec2 uv = gl_FragCoord.xy / u_res;
        vec2 c = uv - 0.5;
        vec2 cuv = uv + c * dot(c, c) * 0.28;
        float lines = 0.5 + 0.5 * cos(cuv.y * u_res.y * 3.14159);
        float scan = mix(0.82, 1.0, lines);
        float grille = mix(0.92, 1.0, 0.5 + 0.5 * cos(gl_FragCoord.x * 2.094));
        vec2 q = abs(cuv - 0.5) - 0.498;
        float sdf = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);
        float edge = smoothstep(-0.02, 0.04, sdf);
        float vig = 1.0 - edge * 0.8;
        float glare = smoothstep(0.5, 0.0, distance(cuv, vec2(0.5, 0.12))) * 0.05;
        float band = smoothstep(0.07, 0.0, abs(fract(cuv.y - u_time * 0.05) - 0.5));
        float lum = scan * grille * vig + glare;
        float a = clamp(max(1.0 - lum, 0.0) - band * 0.05, 0.0, 1.0);
        gl_FragColor = vec4(0.0, 0.0, 0.0, a);
      }`;

    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.warn('Anemoia CRT shader failed', gl.getShaderInfoLog(s));
        return null;
      }
      return s;
    };

    const vs = compile(gl.VERTEX_SHADER, VS);
    const fs = compile(gl.FRAGMENT_SHADER, FS);
    if (!vs || !fs) return null;

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const uRes = gl.getUniformLocation(prog, 'u_res');
    const uTime = gl.getUniformLocation(prog, 'u_time');
    gl.clearColor(0, 0, 0, 0);

    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let raf = null;
    let running = false;
    const t0 = performance.now();

    const resize = () => {
      const w = Math.max(1, Math.floor(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.floor(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };

    const frame = (now) => {
      if (!running) return;
      resize();
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, (now - t0) / 1000);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(frame);
    };

    const start = () => {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
    };

    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVis);

    return { start, stop };
  }

  /* Boots the WebGL glass inside a [data-desktop-crt] overlay; on success adds
     .gl-on so the CSS scanlines/curve hide (the shader draws its own). */
  function initCrt(root) {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
    const overlay = root.querySelector('[data-desktop-crt]');
    const canvas = root.querySelector('[data-desktop-crt-canvas]');
    if (!overlay || !canvas) return null;
    const crt = createCrtPass(canvas);
    if (!crt) return null; // WebGL unavailable — the CSS glass stays
    overlay.classList.add('gl-on');
    crt.start();
    return crt;
  }

  /* Lazy-load and play only the active mode's backdrop loop inside a
     [data-desktop-bg] element; crossfade on mode change. Under reduced motion
     the plate gradient stands in. */
  function initBackdrop(root) {
    const bg = root.querySelector('[data-desktop-bg]');
    if (!bg || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const envs = [...bg.querySelectorAll('[data-mode]')];
    /* Desktop and Letters carry ONE always-on loop with no [data-mode] wrapper,
       so `envs` came back empty and this whole function used to no-op on them —
       including the gesture retry below. That is why a phone that refused the
       boot-time autoplay (Low Power Mode blocks it outright) sat there showing
       iOS's big centred play glyph with nothing ever restarting it. */
    const plain = envs.length ? [] : [...bg.querySelectorAll('video')];
    const sync = () => {
      if (plain.length) {
        plain.forEach((video) => {
          video.muted = true; // the backdrop is scenery; the ambient track carries the sound
          video.play?.().catch(() => {});
        });
        return;
      }
      const mode = document.documentElement.getAttribute('data-anemoia-mode') || 'solar';
      envs.forEach((env) => {
        const video = env.querySelector('video');
        if (!video) return;
        if (env.dataset.mode === mode) {
          if (!video.src && video.dataset.src) {
            video.src = video.dataset.src;
            video.load(); // preload="none": without an explicit load() the src change
                          // may never fetch/decode, so no frame paints (the plate shows).
          }
          video.muted = true; // guarantee the muted-autoplay path on mobile
          video.play?.().catch(() => {});
        } else {
          video.pause?.();
        }
      });
    };
    sync();
    document.addEventListener('anemoia:mode-change', sync);

    /* Mobile browsers can refuse the boot-time play() (Low Power Mode, data
       saver), which would leave the black plate gradient as the whole
       backdrop. Retry on the first user gesture — play() inside a gesture is
       always allowed — and whenever the tab becomes visible again. */
    /* Retried, not one-shot. Unbinding on the first event of any kind spent the
       single attempt on whichever event arrived first — which on iOS is often
       one that grants no user activation at all — and the loop then stayed a
       paused poster for the rest of the page. Keep listening until a video is
       genuinely running. */
    const gestures = ['pointerdown', 'touchstart', 'touchend', 'click', 'keydown'];
    const playing = () => {
      const vids = plain.length ? plain : envs.map((env) => env.querySelector('video')).filter(Boolean);
      return vids.some((v) => !v.paused && !v.ended && v.readyState >= 2);
    };
    const onGesture = () => {
      sync();
      if (playing()) gestures.forEach((evt) => window.removeEventListener(evt, onGesture));
    };
    gestures.forEach((evt) => window.addEventListener(evt, onGesture, { passive: true }));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) sync();
    });
  }

  window.AnemoiaDesktopFX = { createCrtPass, initCrt, initBackdrop };
})();
